import { apiFetch } from '../api/client'
// Structures & infrastructure overlays: fetch helpers, PNG icon set, GeoJSON
// conversion, and the client-side distance mirror of the server's haversine.

import { PR_API_BASE } from './regionStats'
import { powerPlantIconKey } from './powerPlants'

/** All structures for a specific country, with world_x/y/z when resolved. */
export const fetchCountryStructures = async (countryId) => {
  const resp = await apiFetch(`${PR_API_BASE}/countries/${countryId}/structures`)
  if (!resp.ok) throw new Error(`country-structures HTTP ${resp.status}`)
  const payload = await resp.json()
  if (!payload || !payload.success) throw new Error('country-structures fetch failed')
  return payload.data
}

/** Buildable {type, specialisation, level, capacity, cout_construction} catalogue. */
export const fetchStructureTypeData = async () => {
  const resp = await apiFetch(`${PR_API_BASE}/structures/data`, { cacheTtl: 300000 })
  if (!resp.ok) throw new Error(`structure-data HTTP ${resp.status}`)
  const payload = await resp.json()
  if (!payload || !payload.success) throw new Error('structure-data fetch failed')
  return payload.data
}

/** Buildable power-plant catalogue used by Construction Mode. */
export const fetchPowerPlantData = async () => {
  const resp = await apiFetch(`${PR_API_BASE}/power-plants/data`, { cacheTtl: 300000 })
  if (!resp.ok) throw new Error(`power-plant-data HTTP ${resp.status}`)
  const payload = await resp.json()
  if (!payload || !payload.success) throw new Error('power-plant-data fetch failed')
  return payload.data
}

/** Installed power plants for one country, including construction status. */
export const fetchCountryPowerPlants = async (countryId) => {
  // This list is public, like /countries/:id/structures. Sending the shared
  // SSO cookie turns a normal CORS response into a credentialed one, which the
  // public endpoint deliberately does not advertise with
  // Access-Control-Allow-Credentials.
  const resp = await apiFetch(`${PR_API_BASE}/countries/${countryId}/power-plants`)
  if (!resp.ok) throw new Error(`country-power-plants HTTP ${resp.status}`)
  const payload = await resp.json()
  if (!payload || !payload.success) throw new Error('country-power-plants fetch failed')
  return payload.data
}

/**
 * Housing configuration catalogue: {densities, styles, qualities}. Housing is
 * NOT levelled like the other structures — a Logement is defined by a density
 * (base cost per person + comfort), a style and a quality (both price
 * multipliers + comfort) and a free capacity. Mirrors MARC /housing_options.
 */
export const fetchHousingOptions = async () => {
  const resp = await apiFetch(`${PR_API_BASE}/housing/options`, { cacheTtl: 300000 })
  if (!resp.ok) throw new Error(`housing-options HTTP ${resp.status}`)
  const payload = await resp.json()
  if (!payload || !payload.success) throw new Error('housing-options fetch failed')
  return payload.data
}

/**
 * Client-side mirror of HousingRepository.calculate_housing_cost /
 * _comfort — preview only, the server recomputes authoritatively on build.
 * cost = int(base_cost_per_person × capacity × style_mult × quality_mult)
 * comfort = sum of the three comfort_points (caps out at 100 by construction).
 */
export const housingCost = (options, { density_id, style_id, quality_id, capacity }) => {
  const density = options?.densities?.find((d) => d.id === density_id)
  const style = options?.styles?.find((s) => s.id === style_id)
  const quality = options?.qualities?.find((q) => q.id === quality_id)
  if (!density || !style || !quality || !capacity) return null
  return Math.trunc(
    density.base_cost_per_person * capacity * style.price_multiplier * quality.price_multiplier
  )
}

export const housingComfort = (options, { density_id, style_id, quality_id }) => {
  const density = options?.densities?.find((d) => d.id === density_id)
  const style = options?.styles?.find((s) => s.id === style_id)
  const quality = options?.qualities?.find((q) => q.id === quality_id)
  if (!density || !style || !quality) return null
  return density.comfort_points + style.comfort_points + quality.comfort_points
}

/** Infrastructure type catalogue: {id, type, cost_per_km}. */
export const fetchInfraTypes = async () => {
  const resp = await apiFetch(`${PR_API_BASE}/infrastructure/types`, { cacheTtl: 300000 })
  if (!resp.ok) throw new Error(`infra-types HTTP ${resp.status}`)
  const payload = await resp.json()
  if (!payload || !payload.success) throw new Error('infra-types fetch failed')
  return payload.data
}

/** Own-country infrastructure with world geometry (SSO-scoped server-side). */
export const fetchAllInfrastructure = async () => {
  const resp = await apiFetch(`${PR_API_BASE}/map/infrastructure`, { credentials: 'include' })
  if (!resp.ok) throw new Error(`infrastructure HTTP ${resp.status}`)
  const payload = await resp.json()
  if (!payload || !payload.success) throw new Error('infrastructure fetch failed')
  return payload.data
}

/** Header profile: country, player, balances and diplomatic points (SSO). */
export const fetchMyProfile = async () => {
  const resp = await apiFetch(`${PR_API_BASE}/map/my-profile`, { credentials: 'include' })
  if (!resp.ok) throw new Error(`profile HTTP ${resp.status}`)
  const payload = await resp.json()
  if (!payload || !payload.success) throw new Error('profile fetch failed')
  return payload.data
}

/**
 * Construct one structure for the caller's own country at a map-clicked
 * position. Uses the shared SSO cookie; country_id is resolved server-side,
 * never sent from here.
 *
 * A `Logement` carries housing fields (density/style/quality/capacity) instead
 * of specialisation/level — the server routes it through HousingService.
 * `notify_secret` sends the Discord announcement to the country's secret
 * channel instead of its public one.
 */
export const constructStructure = async ({
  region_id, structure_type, specialisation, level, world_x, world_y, world_z,
  density_id, style_id, quality_id, capacity, notify_secret,
}) => {
  const body = structure_type === 'Logement'
    ? { region_id, structure_type, density_id, style_id, quality_id, capacity, world_x, world_y, world_z, notify_secret }
    : { region_id, structure_type, specialisation, level, world_x, world_y, world_z, notify_secret }
  const resp = await apiFetch(`${PR_API_BASE}/map/my-structures`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  const payload = await resp.json().catch(() => null)
  if (!resp.ok || !payload || !payload.success) {
    throw new Error(payload?.error || `construct HTTP ${resp.status}`)
  }
  return payload.data
}

/** Build one power plant for SSO-resolved country from map Construction Mode. */
export const constructPowerPlant = async ({ region_id, plant_type, level, amount = 1, world_x, world_y, world_z = 0 }) => {
  const resp = await apiFetch(`${PR_API_BASE}/map/my-power-plants`, {
    method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ region_id, plant_type, level, amount, world_x, world_y, world_z }),
  })
  const payload = await resp.json().catch(() => null)
  if (!resp.ok || !payload?.success) throw new Error(payload?.error || `power-plant HTTP ${resp.status}`)
  return payload.data
}

/** Move one of the caller's own structures (drag & drop in Construction Mode). */
export const moveStructure = async (structureId, worldX, worldY) => {
  const resp = await apiFetch(`${PR_API_BASE}/map/my-structures/${structureId}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ world_x: worldX, world_y: worldY }),
  })
  const payload = await resp.json().catch(() => null)
  if (!resp.ok || !payload || !payload.success) {
    throw new Error(payload?.error || `move HTTP ${resp.status}`)
  }
  return payload.data
}

/** Move one of the caller's power-plant markers inside its current region. */
export const movePowerPlant = async (plantId, worldX, worldY) => {
  const resp = await apiFetch(`${PR_API_BASE}/map/my-power-plants/${plantId}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ world_x: worldX, world_y: worldY }),
  })
  const payload = await resp.json().catch(() => null)
  if (!resp.ok || !payload?.success) throw new Error(payload?.error || `move power-plant HTTP ${resp.status}`)
  return payload.data
}

/**
 * Create an infrastructure from a freely-drawn polyline. length_km and cost
 * are computed server-side from the path — the values shown while drawing
 * are only a preview.
 */
export const constructInfrastructure = async ({ region_id, infra_type, path, notify_secret }) => {
  const resp = await apiFetch(`${PR_API_BASE}/map/my-infrastructure`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ region_id, infra_type, path, notify_secret }),
  })
  const payload = await resp.json().catch(() => null)
  if (!resp.ok || !payload || !payload.success) {
    throw new Error(payload?.error || `infra HTTP ${resp.status}`)
  }
  return payload.data
}

/**
 * Redraw an existing infrastructure's path (free — the distance was paid at
 * construction). Server caps the new geometry at the owned length_km.
 */
export const updateInfrastructurePath = async (infraId, path) => {
  const resp = await apiFetch(`${PR_API_BASE}/map/my-infrastructure/${infraId}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ path }),
  })
  const payload = await resp.json().catch(() => null)
  if (!resp.ok || !payload || !payload.success) {
    throw new Error(payload?.error || `rework HTTP ${resp.status}`)
  }
  return payload.data
}

/**
 * Fuse several of the caller's own routes into `targetId`. The absorbed rows
 * are deleted server-side and their paid km are transferred to the survivor,
 * so the merged route can then be redrawn for free within the combined
 * budget. Same type + same country are enforced server-side.
 */
export const mergeInfrastructure = async (targetId, sourceIds) => {
  const resp = await apiFetch(`${PR_API_BASE}/map/my-infrastructure/merge`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ target_id: targetId, source_ids: sourceIds }),
  })
  const payload = await resp.json().catch(() => null)
  if (!resp.ok || !payload || !payload.success) {
    throw new Error(payload?.error || `merge HTTP ${resp.status}`)
  }
  return payload.data
}

// --- Distances -----------------------------------------------------------
// Mirror of PR_API utils/world_coords.py (haversine) so the drawing tool can
// preview length/cost live; the server recomputes authoritatively on save.

const EARTH_RADIUS_KM = 6371.0

export const haversineKm = (x1, y1, x2, y2) => {
  const rad = Math.PI / 180
  const dLat = (y2 - y1) * rad
  const dLon = (x2 - x1) * rad
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(y1 * rad) * Math.cos(y2 * rad) * Math.sin(dLon / 2) ** 2
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(a))
}

export const pathLengthKm = (path) => {
  if (!Array.isArray(path) || path.length < 2) return 0
  let total = 0
  for (let i = 0; i < path.length - 1; i++) {
    total += haversineKm(path[i][0], path[i][1], path[i + 1][0], path[i + 1][1])
  }
  return total
}

// --- Icons ---------------------------------------------------------------
// Hand-drawn PNG set from OnlineMap/icones_map (served from
// public/icons/structures/). Usine and Technocentre have one variant per
// specialisation (_a Aerienne, _n Navale, _t Terrestre); Base/Ecole/Logement
// are single-variant. Source files are 800×800 — downscaled to 128px at load
// so 1700+ markers don't blow up GPU texture memory.
export const STRUCTURE_TYPES = ['Usine', 'Base', 'Ecole', 'Logement', 'Technocentre']

const ICON_TARGET_PX = 128

const SPEC_SUFFIX = { Aerienne: 'a', Navale: 'n', Terrestre: 't' }

const ICON_FILES = {
  'Usine_a': 'pr_usine_a.png',
  'Usine_n': 'pr_usine_n.png',
  'Usine_t': 'pr_usine_t.png',
  'Technocentre_a': 'pr_tech_a.png',
  'Technocentre_n': 'pr_tech_n.png',
  'Technocentre_t': 'pr_tech_t.png',
  'Base': 'pr_base.png',
  'Ecole': 'pr_ecole.png',
  'Logement': 'pr_logement.png',
}

/**
 * Icon id for a structure — mirrors the `icon_key` feature property built in
 * structuresToGeoJSON. Specialised types without a matching variant (e.g.
 * spec "NA") fall back to the Terrestre art.
 */
export const iconKeyFor = (type, specialisation) => {
  const suffix = SPEC_SUFFIX[specialisation]
  if (ICON_FILES[`${type}_${suffix}`]) return `${type}_${suffix}`
  if (ICON_FILES[type]) return type
  if (ICON_FILES[`${type}_t`]) return `${type}_t`
  return null
}

const canvasImage = (draw) => {
  const canvas = document.createElement('canvas')
  canvas.width = ICON_TARGET_PX
  canvas.height = ICON_TARGET_PX
  const ctx = canvas.getContext('2d')
  draw(ctx)
  return ctx.getImageData(0, 0, ICON_TARGET_PX, ICON_TARGET_PX)
}

// The official PNGs are prettier, but a generated silhouette is registered
// first. A slow/missing asset can therefore never blank the entire structure
// overlay (MapLibre otherwise drops a symbol whose icon name is unknown).
const structureFallback = (key) => canvasImage((ctx) => {
  const isFactory = key.startsWith('Usine')
  const isTech = key.startsWith('Technocentre')
  const tint = key.endsWith('_a') ? '#7fa8c9' : key.endsWith('_n') ? '#4d6f8f' : '#7d8a5c'
  ctx.fillStyle = 'rgba(16, 20, 22, .88)'
  ctx.fillRect(10, 105, 108, 12)
  ctx.strokeStyle = '#d5b654'; ctx.lineWidth = 4; ctx.lineJoin = 'round'
  if (isFactory) {
    ctx.fillStyle = tint; ctx.fillRect(18, 62, 75, 44)
    ctx.beginPath(); ctx.moveTo(18, 62); [34, 50, 66, 50, 93, 62].forEach((x, i) => ctx.lineTo(x, i % 2 ? 45 : 62)); ctx.stroke()
    ctx.fillStyle = '#444d52'; ctx.fillRect(98, 32, 12, 74)
  } else if (isTech) {
    ctx.fillStyle = tint; ctx.fillRect(25, 54, 55, 52); ctx.fillStyle = '#42545c'; ctx.fillRect(82, 70, 24, 36)
    ctx.beginPath(); ctx.moveTo(20, 54); ctx.lineTo(52, 28); ctx.lineTo(84, 54); ctx.closePath(); ctx.stroke()
    ctx.fillStyle = '#f4d879'; ctx.beginPath(); ctx.arc(52, 48, 6, 0, Math.PI * 2); ctx.fill()
  } else if (key === 'Base') {
    ctx.fillStyle = '#7d8a5c'; ctx.beginPath(); ctx.moveTo(20, 106); ctx.lineTo(20, 60); ctx.lineTo(64, 38); ctx.lineTo(108, 60); ctx.lineTo(108, 106); ctx.closePath(); ctx.fill(); ctx.stroke()
  } else if (key === 'Ecole') {
    ctx.fillStyle = '#b0a893'; ctx.fillRect(18, 62, 92, 44); ctx.beginPath(); ctx.moveTo(14, 62); ctx.lineTo(64, 34); ctx.lineTo(114, 62); ctx.closePath(); ctx.stroke()
  } else {
    ctx.fillStyle = '#d8d4cc'; ctx.fillRect(20, 55, 88, 51); ctx.fillStyle = '#6b6259'; ctx.beginPath(); ctx.moveTo(14, 55); ctx.lineTo(64, 25); ctx.lineTo(114, 55); ctx.closePath(); ctx.fill()
  }
})

/** Load & register every structure icon with the maplibre map instance (idempotent). */
export const loadStructureIcons = (map) =>
  Promise.all(Object.entries(ICON_FILES).map(([key, file]) => new Promise((resolve) => {
    const id = `structure-icon-${key}`
    if (!map.hasImage(id)) map.addImage(id, structureFallback(key))
    const img = new Image()
    img.onload = () => {
      // Downscale on an offscreen canvas before handing to the GPU.
      const canvas = document.createElement('canvas')
      canvas.width = ICON_TARGET_PX
      canvas.height = ICON_TARGET_PX
      canvas.getContext('2d').drawImage(img, 0, 0, ICON_TARGET_PX, ICON_TARGET_PX)
      const image = canvas.getContext('2d').getImageData(0, 0, ICON_TARGET_PX, ICON_TARGET_PX)
      if (map.hasImage(id)) map.updateImage(id, image)
      else map.addImage(id, image)
      resolve()
    }
    img.onerror = () => resolve() // non-fatal — that icon just won't render
    img.src = `/icons/structures/${file}`
  })))

const powerPlantIcon = (key) => canvasImage((ctx) => {
  const line = '#d5b654'
  const wall = '#aeb8bd'
  const dark = '#334047'
  ctx.fillStyle = 'rgba(16, 20, 22, .88)'; ctx.fillRect(8, 108, 112, 10)
  ctx.strokeStyle = line; ctx.lineWidth = 4; ctx.lineJoin = 'round'; ctx.lineCap = 'round'
  if (key === 'nuclear') {
    ctx.fillStyle = wall
    ;[[22, 64, 34], [70, 52, 34]].forEach(([x, y, w]) => { ctx.beginPath(); ctx.moveTo(x, 106); ctx.quadraticCurveTo(x + 5, y, x + w, y); ctx.lineTo(x + w + 5, 106); ctx.closePath(); ctx.fill(); ctx.stroke() })
    ctx.strokeStyle = '#e9eef0'; ctx.lineWidth = 5; ctx.beginPath(); ctx.moveTo(38, 56); ctx.bezierCurveTo(20, 44, 56, 35, 42, 20); ctx.moveTo(87, 44); ctx.bezierCurveTo(70, 30, 106, 25, 92, 10); ctx.stroke()
  } else if (key === 'solar') {
    ctx.fillStyle = '#315d8a'; [[14, 67], [66, 52]].forEach(([x, y]) => { ctx.save(); ctx.transform(1, -0.25, 0, 1, 0, 0); ctx.fillRect(x, y, 48, 34); ctx.strokeRect(x, y, 48, 34); ctx.strokeStyle = '#8bc3e5'; ctx.lineWidth = 2; for (let i = 1; i < 4; i++) ctx.strokeRect(x + i * 12, y, 0, 34); ctx.restore() })
  } else if (key === 'wind') {
    ctx.strokeStyle = '#e8eef0'; ctx.lineWidth = 7; ctx.beginPath(); ctx.moveTo(64, 106); ctx.lineTo(64, 50); ctx.stroke(); ctx.fillStyle = line; ctx.beginPath(); ctx.arc(64, 48, 7, 0, Math.PI * 2); ctx.fill(); ctx.strokeStyle = '#e8eef0'; ctx.lineWidth = 6; [[64, 16], [96, 66], [32, 72]].forEach(([x, y]) => { ctx.beginPath(); ctx.moveTo(64, 48); ctx.lineTo(x, y); ctx.stroke() })
  } else if (key === 'hydro') {
    ctx.fillStyle = '#74828a'; ctx.beginPath(); ctx.moveTo(16, 106); ctx.lineTo(34, 40); ctx.lineTo(95, 40); ctx.lineTo(112, 106); ctx.closePath(); ctx.fill(); ctx.stroke(); ctx.strokeStyle = '#62b6d6'; ctx.lineWidth = 6; [42, 58, 74].forEach((x) => { ctx.beginPath(); ctx.moveTo(x, 45); ctx.bezierCurveTo(x - 8, 70, x + 8, 82, x - 2, 106); ctx.stroke() })
  } else {
    ctx.fillStyle = '#6d777d'; ctx.fillRect(16, 68, 76, 38); ctx.fillStyle = '#40494f'; ctx.fillRect(96, 30, 13, 76); ctx.fillStyle = '#8f5143'; ctx.fillRect(94, 26, 17, 8); ctx.strokeStyle = '#d8e2e7'; ctx.lineWidth = 5; ctx.beginPath(); ctx.moveTo(103, 28); ctx.bezierCurveTo(82, 18, 118, 15, 100, 4); ctx.stroke()
  }
})

/** Register generated plant icons. Types are data-driven, so this is idempotent. */
export const loadPowerPlantIcons = (map, plants) => {
  new Set((plants || []).map((plant) => powerPlantIconKey(plant.type))).forEach((key) => {
    const id = `power-plant-icon-${key}`
    if (!map.hasImage(id)) map.addImage(id, powerPlantIcon(key))
  })
}

/**
 * Convert a flat structures list (fetchAllStructures shape) into a GeoJSON
 * FeatureCollection of points. Rows without resolved coordinates are
 * skipped — shouldn't happen post-migration (region centroid fallback
 * covers everything) but stay defensive.
 */
export const structuresToGeoJSON = (structures) => ({
  type: 'FeatureCollection',
  features: (structures || [])
    .filter((s) => s.world_x != null && s.world_y != null)
    .map((s) => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [s.world_x, s.world_y] },
      properties: {
        id: s.id,
        region_id: s.region_id,
        region_name: s.region_name,
        type: s.type,
        specialisation: s.specialisation,
        level: s.level,
        capacity: s.capacity,
        country_id: s.country_id,
        icon_key: iconKeyFor(s.type, s.specialisation) || 'Base',
      },
    })),
})

/**
 * Count badges (×2 … ×9, ×9+) rendered on an offscreen canvas and registered
 * as images. Done this way because the map style has no `glyphs` endpoint —
 * a MapLibre text layer can't render — and canvas text needs no external
 * font server.
 */
export const loadCountBadges = (map) => {
  const labels = ['2', '3', '4', '5', '6', '7', '8', '9', '9+']
  labels.forEach((label) => {
    const id = `structure-badge-${label}`
    if (map.hasImage(id)) return
    const w = 64
    const h = 40
    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d')
    // Dark pill with gold border
    ctx.beginPath()
    const r = h / 2 - 2
    ctx.moveTo(r + 2, 2)
    ctx.arcTo(w - 2, 2, w - 2, h - 2, r)
    ctx.arcTo(w - 2, h - 2, 2, h - 2, r)
    ctx.arcTo(2, h - 2, 2, 2, r)
    ctx.arcTo(2, 2, w - 2, 2, r)
    ctx.closePath()
    ctx.fillStyle = 'rgba(26, 26, 26, 0.92)'
    ctx.fill()
    ctx.strokeStyle = '#D5B654'
    ctx.lineWidth = 3
    ctx.stroke()
    ctx.fillStyle = '#FFD700'
    ctx.font = 'bold 24px sans-serif'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(`×${label}`, w / 2, h / 2 + 1)
    map.addImage(id, ctx.getImageData(0, 0, w, h))
  })
}

/** icon-image expression fragment resolving a cluster count to its badge id. */
export const badgeImageExpression = [
  'concat',
  'structure-badge-',
  ['case', ['>', ['get', 'count'], 9], '9+', ['to-string', ['get', 'count']]],
]

/**
 * Screen-space clustering of same-type structures. Markers of the SAME
 * icon variant closer than ~radiusPx at the given zoom merge into one icon
 * carrying a ×N count badge; different types never merge. Grid-bucket
 * clustering in degree space (cell size = radiusPx converted at this zoom) —
 * O(n), recomputed on zoom changes only since relative distances don't
 * change while panning.
 */
export const clusterStructuresGeoJSON = (structures, zoom, radiusPx = 36) => {
  const degPerPx = 360 / (256 * Math.pow(2, zoom))
  const cell = radiusPx * degPerPx
  const buckets = new Map()

  ;(structures || [])
    .filter((s) => s.world_x != null && s.world_y != null)
    .forEach((s) => {
      const key = `${iconKeyFor(s.type, s.specialisation) || 'Base'}|${Math.floor(s.world_x / cell)}|${Math.floor(s.world_y / cell)}`
      if (!buckets.has(key)) buckets.set(key, [])
      buckets.get(key).push(s)
    })

  const features = []
  buckets.forEach((group) => {
    if (group.length === 1) {
      const s = group[0]
      features.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [s.world_x, s.world_y] },
        properties: {
          id: s.id,
          region_id: s.region_id,
          region_name: s.region_name,
          type: s.type,
          specialisation: s.specialisation,
          level: s.level,
          capacity: s.capacity,
          country_id: s.country_id,
          icon_key: iconKeyFor(s.type, s.specialisation) || 'Base',
          count: 1,
        },
      })
    } else {
      const cx = group.reduce((a, s) => a + s.world_x, 0) / group.length
      const cy = group.reduce((a, s) => a + s.world_y, 0) / group.length
      const levels = group.map((s) => s.level)
      features.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [cx, cy] },
        properties: {
          id: null,
          region_id: group[0].region_id,
          region_name: group[0].region_name,
          type: group[0].type,
          specialisation: group[0].specialisation,
          level: Math.max(...levels),
          min_level: Math.min(...levels),
          max_level: Math.max(...levels),
          country_id: group[0].country_id,
          icon_key: iconKeyFor(group[0].type, group[0].specialisation) || 'Base',
          count: group.length,
          // Object/array properties get auto-stringified by MapLibre's GeoJSON
          // source (vector tile encoding only keeps string/number/boolean) —
          // parse this back out in event handlers. Carries each member's own
          // id/region/country so a stacked structure can still be picked out
          // and dragged individually instead of being stuck behind the count>1
          // guard regardless of zoom (coincident coordinates never split).
          members: JSON.stringify(group.map((s) => ({
            id: s.id,
            region_id: s.region_id,
            region_name: s.region_name,
            type: s.type,
            specialisation: s.specialisation,
            level: s.level,
            country_id: s.country_id,
            icon_key: iconKeyFor(s.type, s.specialisation) || 'Base',
          }))),
        },
      })
    }
  })

  return { type: 'FeatureCollection', features }
}

/** Convert the infrastructure geometry list into a GeoJSON FeatureCollection of lines. */
// A stored path is either a flat polyline [[x,y],...] (legacy / single
// segment) or a list of segments [[[x,y],...],...] (branched networks with
// tri-/quadri-nodes — segments share junction coordinates).
export const isNestedPath = (path) => Array.isArray(path?.[0]?.[0])

export const pathSegments = (path) => {
  if (!Array.isArray(path) || path.length === 0) return []
  return (isNestedPath(path) ? path : [path]).filter((s) => Array.isArray(s) && s.length >= 2)
}

/** Total km of a stored path (flat or segmented). */
export const storedPathLengthKm = (path) =>
  pathSegments(path).reduce((sum, seg) => sum + pathLengthKm(seg), 0)

// --- In-progress drawing (App state: [{region, lngLat, seg}]) -------------

/** Group the in-progress editor path into ordered coordinate segments. */
export const editorPathSegments = (points) => {
  const bySeg = new Map()
  ;(points || []).forEach((p) => {
    const seg = p.seg || 0
    if (!bySeg.has(seg)) bySeg.set(seg, [])
    bySeg.get(seg).push([p.lngLat.lng, p.lngLat.lat])
  })
  return [...bySeg.entries()].sort((a, b) => a[0] - b[0]).map(([, coords]) => coords)
}

/** Total km of the in-progress editor path (only segments with ≥2 points count). */
export const editorPathLengthKm = (points) =>
  editorPathSegments(points)
    .filter((s) => s.length >= 2)
    .reduce((sum, s) => sum + pathLengthKm(s), 0)

export const infrastructureToGeoJSON = (infrastructures) => ({
  type: 'FeatureCollection',
  features: (infrastructures || [])
    .filter((i) => pathSegments(i.path).length >= 1)
    .map((i) => {
      const segs = pathSegments(i.path)
      return {
        type: 'Feature',
        geometry: segs.length === 1
          ? { type: 'LineString', coordinates: segs[0] }
          : { type: 'MultiLineString', coordinates: segs },
        properties: {
          id: i.id,
          type: i.type,
          length_km: i.length_km,
          region_id: i.region_id,
          region_name: i.region_name,
          country_id: i.country_id,
        },
      }
    }),
})
